/* /account/security - two-step sign-in (TOTP), password and sign-out. */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { WEB_ROUTES, type Me } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { useMe, useSession } from '../app/session';
import { useErrorHandler } from '../app/hooks';
import { Alert, Button, Chip, CodeField, CopyButton, Live, PageHeader, PasswordField, SafeSvg, Section } from '../app/ui';
import { api } from '../lib/api';
import { MIN_PASSWORD, sixDigits } from '../lib/forms';

type Enrollment = { factor_id: string; qr_svg: string; secret: string; uri: string };

export function Security() {
  const { t } = useT();
  const { signOut } = useSession();
  return (
    <>
      <PageHeader title={t('sec.title')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      <TwoStep />
      <PasswordChange />
      <Section title={t('sec.signoutTitle')} id="signout">
        <div className="card">
          <p>{t('sec.signoutText')}</p>
          <div className="actions"><Button variant="gray" onClick={() => void signOut()}>{t('nav.signout')}</Button></div>
        </div>
      </Section>
    </>
  );
}

function TwoStep() {
  const { t } = useT();
  const me = useMe();
  const { setMe } = useSession();
  const handle = useErrorHandler();
  const [enroll, setEnroll] = useState<Enrollment | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const [error, setError] = useState<{ code?: string; form?: string }>({});
  const [done, setDone] = useState('');
  const stepRef = useRef<HTMLOListElement>(null);
  useEffect(() => { if (enroll) stepRef.current?.querySelector<HTMLElement>('[data-first]')?.focus(); }, [enroll]);

  const start = async () => {
    if (busy) return;
    setBusy(true); setError({}); setDone('');
    try { setEnroll(await api.post<Enrollment>('/api/auth/mfa/enroll')); }
    catch (e) { const msg = handle(e); if (msg) setError({ form: msg }); }
    finally { setBusy(false); }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !enroll) return;
    if (code.length !== 6) { setError({ code: t('v.code') }); document.getElementById('enroll-code')?.focus(); return; }
    setBusy(true); setError({});
    try {
      const r = await api.post<{ me: Me }>('/api/auth/mfa/enroll/verify', { factor_id: enroll.factor_id, code });
      setEnroll(null); setCode('');
      setMe(r.me);
      setDone(t('sec.mfaEnabled'));
    } catch (er) {
      setCode('');
      const msg = handle(er);
      if (msg) setError({ form: msg });
    } finally { setBusy(false); }
  };

  const turnOff = async () => {
    if (busy) return;
    setBusy(true); setError({});
    try {
      const r = await api.post<{ me: Me }>('/api/auth/mfa/disable', { factor_id: me.mfa.factor_id });
      setConfirmOff(false);
      setMe(r.me);
      setDone(t('sec.mfaDisabled'));
    } catch (e) {
      /* without a fresh code (aal2) this sends the student to /signin/code and back here */
      const msg = handle(e);
      if (msg) setError({ form: msg });
    } finally { setBusy(false); }
  };

  const otpauth = enroll && /^otpauth:\/\//i.test(enroll.uri) ? enroll.uri : null;
  const groupedSecret = enroll ? enroll.secret.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim() : '';
  const secretRef = useRef<HTMLElement>(null);

  return (
    <Section title={t('sec.mfaTitle')} id="two-step">
      <div className="card stack">
        <div className="statusline">
          <Chip tone={me.mfa.enrolled ? 'green' : 'orange'}>{me.mfa.enrolled ? t('common.on') : t('common.off')}</Chip>
          <p>{me.mfa.enrolled ? t('sec.mfaOnText') : t('sec.mfaOffText')}</p>
        </div>

        {!me.mfa.enrolled && !enroll && <div><Button busy={busy} onClick={() => void start()}>{t('sec.mfaTurnOn')}</Button></div>}

        {enroll && (
          <form noValidate onSubmit={verify} aria-label={t('sec.mfaTurnOn')}>
            <ol className="steps" ref={stepRef}>
              <li><p tabIndex={-1} data-first>{t('sec.mfaStep1')}</p></li>
              <li>
                <p>{t('sec.mfaStep2')}</p>
                <SafeSvg markup={enroll.qr_svg} label={t('sec.mfaQr')} className="qr" />
                {otpauth && <p className="mt-sm"><a href={otpauth}>{t('sec.mfaOpenApp')}</a></p>}
                <p className="mt-sm">{t('sec.mfaKey')}</p>
                <div className="copyfield">
                  <code className="secret" dir="ltr" ref={secretRef}>{groupedSecret}</code>
                  <CopyButton text={enroll.secret.replace(/\s+/g, '')} label={t('sec.mfaCopyKey')} selectRef={secretRef} />
                </div>
              </li>
              <li>
                <CodeField id="enroll-code" label={t('sec.mfaStep3')} value={code} onChange={v => setCode(sixDigits(v))} error={error.code} />
              </li>
            </ol>
            {error.form && <div className="mt"><Alert>{error.form}</Alert></div>}
            <div className="btnrow mt">
              <Button type="submit" busy={busy}>{t('sec.mfaVerify')}</Button>
              <Button variant="gray" onClick={() => { setEnroll(null); setCode(''); setError({}); }}>{t('common.cancel')}</Button>
            </div>
          </form>
        )}

        {me.mfa.enrolled && !confirmOff && <div><Button variant="danger" onClick={() => setConfirmOff(true)}>{t('sec.mfaTurnOff')}</Button></div>}
        {me.mfa.enrolled && confirmOff && (
          <div className="confirm">
            <p>{t('sec.mfaConfirmOff')}</p>
            <div className="btnrow">
              <Button variant="danger-solid" busy={busy} onClick={() => void turnOff()}>{t('sec.mfaTurnOff')}</Button>
              <Button variant="gray" onClick={() => setConfirmOff(false)}>{t('sec.mfaKeepOn')}</Button>
            </div>
          </div>
        )}
        {!enroll && error.form && <Alert>{error.form}</Alert>}
        <Live>{done && <span className="text-ok">{done}</span>}</Live>
      </div>
    </Section>
  );
}

function PasswordChange() {
  const { t } = useT();
  const handle = useErrorHandler();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [errors, setErrors] = useState<{ pw?: string; pw2?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: typeof errors = {};
    if ([...pw].length < MIN_PASSWORD) errs.pw = t('v.password');
    else if (pw !== pw2) errs.pw2 = t('v.passwordMatch');
    setErrors(errs); setDone(false);
    if (errs.pw || errs.pw2) { document.getElementById(errs.pw ? 'new-pw' : 'new-pw2')?.focus(); return; }
    setBusy(true);
    try {
      await api.post('/api/auth/password', { password: pw });
      setPw(''); setPw2(''); setDone(true);
    } catch (er) {
      const msg = handle(er);
      if (msg) setErrors({ form: msg });
    } finally { setBusy(false); }
  };
  return (
    <Section title={t('sec.pwTitle')} id="password">
      <form className="card stack" noValidate onSubmit={submit} aria-labelledby="password-title">
        <PasswordField id="new-pw" label={t('sec.pwNew')} value={pw} onChange={setPw} autoComplete="new-password" showStrength error={errors.pw} />
        <PasswordField id="new-pw2" label={t('sec.pwConfirm')} value={pw2} onChange={setPw2} autoComplete="new-password" error={errors.pw2} />
        {errors.form && <Alert>{errors.form}</Alert>}
        <div><Button type="submit" busy={busy}>{t('sec.pwSubmit')}</Button></div>
        <Live>{done && <span className="text-ok">{t('sec.pwDone')}</span>}</Live>
      </form>
    </Section>
  );
}
